import { Hourglass, Lock, Mail, User, UserPlus } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { type PropsWithChildren } from "react";
import { XStack, YStack } from "tamagui";
import { LoginSheets } from "../../core/account/LoginSheets";
import { useStores } from "../../core/stores";
import { AccountSheetActive } from "../../plans/PlanModel";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/InputNew";
import { Screen } from "../../ui/Screen";
import { Separator } from "../../ui/Separator";
import {
  type BusinessCustomerModel,
  CustomerMemberLevel,
} from "./customers/BusinessCustomerModel";

const BusinessAdminLogin = observer(
  ({ customer }: { customer: BusinessCustomerModel }) => {
    const { sessionStore } = useStores();
    const [showSheet, setShowSheet] = React.useState(AccountSheetActive.None);

    const isRegistered = sessionStore.isRegistered;
    const isReadyToApply =
      isRegistered && !!sessionStore.superUser?.data.displayName;

    const isApplying = !!customer.data.applying[sessionStore.superUser.uid];

    return (
      <>
        {!isRegistered && (
          <Box>
            <YStack gap={"$medium"}>
              <Lock size={"$large"} alignSelf={"center"} />
              <Body>Please login to access this.</Body>

              <Button
                onPress={() => {
                  void customer.addMemberLevels(
                    sessionStore.superUser.uid,
                    CustomerMemberLevel.BusinessAdmin,
                  );
                }}
              >
                DEV - Add myself as admin
              </Button>

              <Button
                type="secondary"
                icon={Mail}
                onPress={() => {
                  setShowSheet(AccountSheetActive.Login);
                }}
              >
                Sign in
              </Button>
            </YStack>
          </Box>
        )}

        {isRegistered && (
          <Box>
            <YStack gap={"$medium"}>
              <Lock size={"$large"} alignSelf={"center"} />
              <Body>
                You need to apply for access. Once an owner of this company
                approves your application you will gain access.
              </Body>

              <Separator />

              {!isApplying && (
                <>
                  <Body muted>Set your display name</Body>
                  <Input
                    icon={User}
                    iconFirst
                    type="border"
                    preventNewLine
                    defaultValue={
                      sessionStore.superUser?.data.displayName ?? ""
                    }
                    placeholder="Display name"
                    submitOnBlur
                    submitOnEnter
                    onSubmitEditing={(event) => {
                      sessionStore.superUser?.setDisplayName(
                        event.nativeEvent.text,
                      );
                    }}
                  />
                  <Button
                    disabled={!isReadyToApply}
                    type="primary"
                    icon={UserPlus}
                    onPress={() => {
                      customer.addApplication(sessionStore.superUser.uid, {
                        displayName: sessionStore.superUser.data.displayName,
                        avatarUrl: sessionStore.superUser.data.avatarUrl,
                        memberLevel: CustomerMemberLevel.BusinessAdmin,
                      });
                    }}
                  >
                    Apply for access
                  </Button>
                </>
              )}

              {isApplying && (
                <XStack gap={"$medium"}>
                  <Hourglass />
                  <Body>
                    Great, you are applying. Poke someone you know to accept you
                    as a full member.
                  </Body>
                </XStack>
              )}
            </YStack>
          </Box>
        )}

        <LoginSheets currentSheet={showSheet} onChangeSheet={setShowSheet} />
      </>
    );
  },
);

export const BusinessAdminGate = observer(
  ({
    customer,
    children,
  }: PropsWithChildren & { customer: BusinessCustomerModel }) => {
    const hasAccess = customer.hasAdminAccess;

    return (
      <Screen>
        {!hasAccess && <BusinessAdminLogin customer={customer} />}
        {hasAccess && children}
      </Screen>
    );
  },
);

export default BusinessAdminGate;
